/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.data.util

import io.github.jan.supabase.exceptions.BadRequestRestException
import io.github.jan.supabase.exceptions.HttpRequestException
import io.github.jan.supabase.exceptions.NotFoundRestException
import io.github.jan.supabase.exceptions.RestException
import io.github.jan.supabase.exceptions.SupabaseEncodingException
import io.github.jan.supabase.exceptions.UnauthorizedRestException
import kotlinx.serialization.SerializationException
import studio.oddpods.cauce.core.base.network.NetworkError
import kotlin.coroutines.cancellation.CancellationException

/**
 * Executes [block] and wraps the outcome in a [Result].
 *
 * Supabase SDK exceptions are mapped to [RemoteException] with the
 * appropriate [NetworkError], so callers never see raw Supabase types.
 *
 * [BusinessError] instances pass through as [Result.failure] so that
 * ViewModels can distinguish domain violations from remote failures.
 */
suspend fun <T> safeCall(block: suspend () -> T): Result<T> =
    try {
        Result.success(block())
    } catch (e: CancellationException) {
        throw e
    } catch (e: BusinessError) {
        Result.failure(e)
    } catch (e: RemoteException) {
        Result.failure(e)
    } catch (e: Exception) {
        Result.failure(e.toRemoteException())
    }

/**
 * Like [safeCall] but throws instead of returning [Result].
 *
 * Use this inside Flow-producing private methods (e.g. `fetchAll()`)
 * so that `Flow.catch` in ViewModels receives a [RemoteException]
 * instead of a raw Supabase exception.
 */
suspend fun <T> safeThrow(block: suspend () -> T): T =
    try {
        block()
    } catch (e: CancellationException) {
        throw e
    } catch (e: BusinessError) {
        throw e
    } catch (e: RemoteException) {
        throw e
    } catch (e: Exception) {
        throw e.toRemoteException()
    }

/**
 * Maps a Supabase SDK exception to a [RemoteException] carrying the
 * appropriate [NetworkError].
 */
fun Exception.toRemoteException(): RemoteException {
    val networkError = when (this) {
        is BadRequestRestException -> NetworkError.BAD_REQUEST
        is NotFoundRestException -> NetworkError.NOT_FOUND
        is UnauthorizedRestException -> NetworkError.UNAUTHORIZED
        is RestException -> when (statusCode) {
            429 -> NetworkError.TOO_MANY_REQUESTS
            in 500..599 -> NetworkError.SERVER
            408 -> NetworkError.REQUEST_TIMEOUT
            else -> NetworkError.UNKNOWN
        }
        is HttpRequestException -> NetworkError.REQUEST_TIMEOUT
        is SerializationException -> NetworkError.SERIALIZATION
        is SupabaseEncodingException -> NetworkError.SERIALIZATION
        else -> NetworkError.UNKNOWN
    }
    val mapped = networkError.toThrowable() as RemoteException
    return RemoteException(mapped.networkError, mapped.message ?: networkError.name, cause = this)
}
